<script setup lang="ts">
import { storeToRefs } from 'pinia'

import BaseButton from '@tix/ds/components/atoms/BaseButton.vue'
import RouterLinkButton from '@tix/ds/components/atoms/RouterLinkButton.vue'
import '@tix/ds/styles/portico/component/tab-nav.css'
import { useI18n } from '@tix/language'

import { withSellerPrefix } from '@/helpers/sellerPrefix'

import { useFlowDefinitionStore } from '@/stores/flowDefinitions'
import { useGlobalError } from '@/stores/useGlobalErrorStore'

import MembersBanner from '@/components/MembersBanner.vue'
import BundleListing from '@/components/bundles/BundleListing.vue'

const { t } = useI18n()
const globalError = useGlobalError()
const flowDefinitionStore = useFlowDefinitionStore()
const { data: flows } = storeToRefs(flowDefinitionStore)
const { error } = await flowDefinitionStore.loadAll()
const eventsHref = withSellerPrefix('/events')

if (error) {
  const loadError = error instanceof Error ? error : new Error('Error fetching flow definitions', { cause: error })
  globalError.set(loadError, { message: 'Unable to load bundles', dismissable: false })
}
</script>

<template>
  <div class="bundles-page">
    <h1>{{ t('eventListingRoute.title') }}</h1>
    <nav aria-label="Purchase type" class="purchase-tabs">
      <BaseButton component="a" hierarchy="tertiary" :href="eventsHref" intent="neutral">
        Standard Purchase
      </BaseButton>
      <RouterLinkButton aria-current="page" class="active" hierarchy="tertiary" intent="neutral" to="/bundles">
        Bundle Purchase
      </RouterLinkButton>
    </nav>
    <MembersBanner />
    <BundleListing v-if="!error" :flows="flows" />
  </div>
</template>

<style scoped>
.bundles-page {
  display: flex;
  padding-top: var(--padding-spacious);
  flex-direction: column;
  gap: var(--padding-spacious);
}

.purchase-tabs {
  align-self: flex-start;
  display: flex;
}

.purchase-tabs :deep(.base-button) {
  position: relative;
  padding-inline: calc(var(--padding-comfy) - var(--space-0-5));
  border: 0;
  border-bottom: var(--border-default) solid var(--tab-nav-border);
  border-radius: 0;
  background: var(--tab-nav-button-bg);
  color: var(--tab-nav-button-text);
  font-weight: var(--font-weight-regular);
  line-height: 1.5;
}

.purchase-tabs :deep(.base-button:hover:not(.active)),
.purchase-tabs :deep(.base-button:focus-visible) {
  background: var(--tab-nav-button-bg);
  color: var(--tab-nav-button-text-hover);
}

.purchase-tabs :deep(.base-button .label-text) {
  line-height: 1.5;
}

.purchase-tabs :deep(.base-button:hover:not(.active)::after),
.purchase-tabs :deep(.base-button:focus-visible::after),
.purchase-tabs :deep(.base-button.active::after) {
  position: absolute;
  right: 0;
  bottom: calc(0px - var(--border-default));
  left: 0;
  height: var(--space-0-25);
  background: var(--tab-nav-button-border-hover);
  content: '';
}

.purchase-tabs :deep(.base-button.active) {
  color: var(--tab-nav-button-text-active);
  font-weight: var(--font-weight-bold);
}

.purchase-tabs :deep(.base-button.active::after) {
  background: var(--tab-nav-button-border-active);
}
</style>
